import { useSyncExternalStore } from 'react';
import {
	Dimensions,
	Easing,
	EasingFunction,
	Keyboard,
	KeyboardEvent,
	Platform,
	TextInput,
} from 'react-native';

/**
 * One shared reading of the on-screen keyboard, in the same coordinates the layout is measured in
 * (`measure`'s page coordinates on native, `getBoundingClientRect` on web), so a view can tell whether the
 * keyboard covers it.
 *
 * Native reads React Native's keyboard events. Web has none: the keyboard of iOS Safari, and of
 * Chrome on Android since 108, shrinks only the visual viewport and leaves the layout viewport -
 * and so the app - the size it was. There the keyboard is whatever of the layout viewport the
 * visual viewport no longer shows below it - past a threshold, so a collapsing browser toolbar is
 * not mistaken for one. It is not tied to focus: the keyboard is still on its way down for a
 * moment after a field loses focus, and whatever made room for it should not jump back first.
 */
export type KeyboardFrame = {
	/** How far the keyboard reaches up from the bottom of the window; 0 while it is closed. */
	height: number;
	/** The keyboard's top edge in window coordinates; the window's height while it is closed. */
	top: number;
	/** How long the keyboard takes to move, so what moves with it can keep pace. */
	duration: number;
	easing: EasingFunction;
};

/**
 * UIKit moves the keyboard on its private curve 7, which this bezier follows closely. Android
 * reports the keyboard only once it is up, and the web as the viewport resizes, so there the
 * content catches up on Material's decelerating curve.
 */
const IOS_EASING = Easing.bezier(0.38, 0.7, 0.125, 1);
const CATCH_UP_EASING = Easing.bezier(0.2, 0, 0, 1);
const DEFAULT_DURATION = Platform.OS === 'ios' ? 250 : 220;
/** Less than this is a browser toolbar sliding away, not a keyboard. */
const WEB_MIN_KEYBOARD = 80;

const closedFrame = (windowHeight: number): KeyboardFrame => ({
	height: 0,
	top: windowHeight,
	duration: DEFAULT_DURATION,
	easing: Platform.OS === 'ios' ? IOS_EASING : CATCH_UP_EASING,
});

let frame: KeyboardFrame = closedFrame(Dimensions.get('window').height);
const listeners = new Set<() => void>();
let detach: (() => void) | null = null;

const publish = (next: KeyboardFrame) => {
	if (next.height === frame.height && next.top === frame.top) return;
	frame = next;
	listeners.forEach(listener => listener());
};

const isEditable = (element: Element | null) => {
	if (!element) return false;
	if (element instanceof HTMLTextAreaElement) return true;
	if (element instanceof HTMLInputElement) {
		return !['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'file', 'color'].includes(
			element.type,
		);
	}
	return (element as HTMLElement).isContentEditable === true;
};

/** The web keyboard's frame, read from the visual viewport. */
/**
 * The height of the page the app is laid out in. iOS Safari shrinks `innerHeight` along with the
 * visual viewport when its keyboard opens; the root element's client height stays the layout
 * viewport's.
 */
export const webLayoutHeight = () => document.documentElement.clientHeight || window.innerHeight;

/** The web keyboard's frame, read from the visual viewport. */
export const readWebKeyboard = (): KeyboardFrame => {
	const layoutHeight = webLayoutHeight();
	const viewport = window.visualViewport;
	// A pinch-zoomed page shrinks the visual viewport too; that is not a keyboard.
	if (!viewport || Math.abs(viewport.scale - 1) > 0.01) {
		return closedFrame(layoutHeight);
	}
	// Measured from the top of the page rather than the viewport's offset: the page is held at the
	// top while the keyboard is up (see `attachWeb`), so the two agree once it has settled.
	const top = viewport.height;
	const height = layoutHeight - top;
	return height < WEB_MIN_KEYBOARD
		? closedFrame(layoutHeight)
		: { ...closedFrame(layoutHeight), height, top };
};

const attachWeb = () => {
	const viewport = window.visualViewport;
	const update = () => {
		const next = readWebKeyboard();
		// iOS Safari scrolls the page to bring a focused field into view, even though the app's
		// root never scrolls. The field is lifted clear of the keyboard instead, so scroll it back.
		if (next.height > 0 && window.scrollY !== 0) window.scrollTo(0, 0);
		publish(next);
	};
	// Focus moves before the viewport does; re-read once it has settled as well. Not straight away:
	// moving from one field to the next blurs the first before focusing the second, and reading in
	// between would see no field and close the keyboard for a moment.
	const onFocusChange = () => {
		setTimeout(update, 0);
		setTimeout(update, 300);
	};
	viewport?.addEventListener('resize', update);
	viewport?.addEventListener('scroll', update);
	window.addEventListener('resize', update);
	document.addEventListener('focusin', onFocusChange);
	document.addEventListener('focusout', onFocusChange);
	return () => {
		viewport?.removeEventListener('resize', update);
		viewport?.removeEventListener('scroll', update);
		window.removeEventListener('resize', update);
		document.removeEventListener('focusin', onFocusChange);
		document.removeEventListener('focusout', onFocusChange);
	};
};

const attachNative = () => {
	const easing = Platform.OS === 'ios' ? IOS_EASING : CATCH_UP_EASING;
	const onChange = (event: KeyboardEvent) => {
		const windowHeight = Dimensions.get('window').height;
		const top = event.endCoordinates.screenY;
		const height = Math.max(0, windowHeight - top);
		// A hardware keyboard on iOS reports a frame off the bottom of the screen.
		if (height <= 0 || event.endCoordinates.height <= 0) {
			publish(closedFrame(windowHeight));
			return;
		}
		publish({
			height,
			top,
			duration: event.duration || DEFAULT_DURATION,
			easing,
		});
	};
	const onHide = (event?: KeyboardEvent) =>
		publish({
			...closedFrame(Dimensions.get('window').height),
			duration: event?.duration || DEFAULT_DURATION,
		});
	// iOS announces the keyboard before it moves, Android only once it has.
	const subscriptions =
		Platform.OS === 'ios'
			? [
					Keyboard.addListener('keyboardWillChangeFrame', onChange),
					Keyboard.addListener('keyboardWillHide', onHide),
				]
			: [
					Keyboard.addListener('keyboardDidShow', onChange),
					Keyboard.addListener('keyboardDidHide', onHide),
				];
	return () => subscriptions.forEach(subscription => subscription.remove());
};

export const subscribeKeyboard = (listener: () => void) => {
	listeners.add(listener);
	if (!detach) {
		detach = Platform.OS === 'web' ? attachWeb() : attachNative();
		if (Platform.OS === 'web') publish(readWebKeyboard());
	}
	return () => {
		listeners.delete(listener);
		if (listeners.size === 0 && detach) {
			detach();
			detach = null;
		}
	};
};

export const getKeyboard = () => frame;

/** The keyboard's current frame; re-renders when it opens, closes or resizes. */
export const useKeyboard = () => useSyncExternalStore(subscribeKeyboard, getKeyboard, getKeyboard);

export type WindowRect = { x: number; y: number; width: number; height: number };

/**
 * Anything whose place on screen can be read: a native host view (by `measure`, whose page
 * coordinates count from the surface's root - the screen's top left in the app's window and in a
 * `Modal` alike; `measureInWindow` on Android leaves the status bar out of the app's window but not
 * out of a modal's) or, on web, a DOM element.
 */
export type Measurable = {
	measure?: (
		callback: (
			x: number,
			y: number,
			width: number,
			height: number,
			pageX: number,
			pageY: number,
		) => void,
	) => void;
	getBoundingClientRect?: () => { left: number; top: number; width: number; height: number };
};

/** A view's frame in screen coordinates, the keyboard's own, or null once it has gone. */
export const measureOnScreen = (node: Measurable | null | undefined) =>
	new Promise<WindowRect | null>(resolve => {
		if (Platform.OS === 'web' && node?.getBoundingClientRect) {
			const { left, top, width, height } = node.getBoundingClientRect();
			resolve(width || height ? { x: left, y: top, width, height } : null);
			return;
		}
		if (!node?.measure) {
			resolve(null);
			return;
		}
		node.measure((_x, _y, width, height, pageX, pageY) =>
			resolve(width || height ? { x: pageX, y: pageY, width, height } : null),
		);
	});

/** The text field that has focus - a host node, measurable - or null. */
export const getFocusedInput = (): Measurable | null => {
	if (Platform.OS === 'web') {
		const element = document.activeElement;
		return isEditable(element) ? (element as HTMLElement) : null;
	}
	return (TextInput.State.currentlyFocusedInput() as Measurable | null) ?? null;
};
